import React, { useEffect, useMemo, useState, useRef } from 'react'
import classNames from 'classnames'
import { Case, Default, Switch, When } from 'react-if'
import { LinkButton } from '@/atoms/Button'
import { UpArrowCircleIcon } from '@/atoms/Icons/UpArrowCircleIcon'
import { Image } from '@/atoms/Image'
import { LazyHydrate } from '@/atoms/LazyHydrate'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { AsH2, CaptionSM, HeadingXL, HeadingXS } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { useProjectMeta } from '@/contexts/ProjectMetaContext'
import { useTheatricalRegionContext } from '@/contexts/TheatricalRegionContext'
import { BoundedImage } from '@/molecules/BoundedImage'
import { FaqSection } from '@/organisms/FaqSection'
import { GoalTrackerWidget } from '@/organisms/GoalTrackerWidget'
import { HorizontalShopifyMerchScroller } from '@/organisms/HorizontalShopifyMerchScroller'
import { InvestmentBanner } from '@/organisms/InvestmentBanner'
import { ShareSection } from '@/organisms/ShareSection'
import { getWebClient } from '@/services/ApolloClient'
import { useGuildMember, useUserGuildTickets } from '@/services/GuildUserService'
import { PlayableLivestream } from '@/services/LivestreamService'
import { ContentWithWatchableReasons, getCurrentPhaseSlug, isSellingTickets } from '@/services/ProjectPhaseService'
import { fetchSeriesEpisodeStills } from '@/services/ProjectsService'
import { EpisodeWithStills } from '@/services/ProjectsService/types'
import { HubPageTheatricalReleaseResult, useTheatricalTicketStatus } from '@/services/Theatrical/queries'
import { useUserRegion } from '@/services/UserRegionService'
import { WhereToWatch } from '@/services/WhereToWatchService'
import {
  CallToAction,
  FilmDetailsAdditionalVideosCollection,
  FilmDetails as FilmDetailsType,
  InvestmentBanner as InvestmentBannerContent,
  ProjectTheme,
} from '@/types/codegen-contentful'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { getGoalTrackerSlug, getGoalTrackerTitleContent } from '@/utils/goal-tracker'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { isDefined } from '@/utils/primitives/generics'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { Maybe } from '@/utils/types'
import { FilmDetails } from '@/views/WatchTheatricalView/FilmDetails'
import { StickyCta } from '@/views/WatchTheatricalView/StickyCta'
import { VideoGallery } from '@/views/WatchTheatricalView/VideoGallery'
import { useWatchTheatricalContext } from '@/views/WatchTheatricalView/WatchTheatricalContext'
import { WhereToWatchSection } from '@/views/WatchTheatricalView/WhereToWatch'
import { CtaSquareCollection, filterToApplicableCtas } from './CtaSquareCollection'
import { FranchiseLink } from './FranchiseLink'
import { GuildPromotionFloatingCta } from './GuildPromotionFloatingCta'
import { ImageGallery } from './ImageGallery'
import { LivestreamLink } from './LivestreamLink'
import { PressLinksSection } from './PressLinksSection'
import { Reviews } from './Reviews'
import { Ctas, CtaStatusLine } from './ctas'
import { GetUpdatesCta } from './getUpdatesCta'

interface WatchTheatricalViewProps {
  livestreams: PlayableLivestream[] | null
  whereToWatch: WhereToWatch
  contentfulProjectTheme: ProjectTheme | null
  isStreaming?: boolean
  /** Content with watchableReasons for streaming availability checks */
  watchableContent?: ContentWithWatchableReasons | null
  /** Theatrical release data fetched server-side. Null for shows or licensed content. */
  hubPageTheatricalRelease?: HubPageTheatricalReleaseResult | null
}

export const WatchTheatricalView: React.FC<WatchTheatricalViewProps> = (props) => {
  const [joinTheMovementBannerVisited, setJoinTheMovementBannerVisited] = useState(false)
  const [episodesWithStills, setEpisodesWithStills] = useState<EpisodeWithStills[] | null>(null)
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false)
  const [videoReady, setVideoReady] = useState(false)
  const heroVideoRef = useRef<HTMLVideoElement>(null)
  const heroSectionRef = useRef<HTMLElement>(null)
  const { isStreaming = false } = props
  const { isGuildMember, loading } = useGuildMember()
  const projectMeta = useProjectMeta()
  const client = getWebClient()
  const { asPath } = useSafeRouter()
  const { t } = useTranslate('theatrical-presales')
  const { t: tCommon } = useTranslate('common')
  const {
    critics,
    ctaSquaresCollection,
    filmDetails,
    franchise,
    friendlyTheatricalReleaseDate,
    goalTrackers,
    hasPressPage,
    heroCta,
    images,
    investmentBanner,
    logo,
    name,
    shopifyStore,
    slug,
  } = useWatchTheatricalContext()
  const { heroBackgroundVideo, heroBackgroundImage, trailerSourceUrl, additionalVideosCollection, shareOverrideUrl } =
    filmDetails || {}
  const { hubPageHeroBackgroundVideo, isInTheaters, userCountry, allTheatricalReleaseRegions, releaseDate } =
    useTheatricalRegionContext()
  const { region } = useUserRegion()

  // Server-side data for critics (region-agnostic)
  const hubPageTheatricalRelease = props.hubPageTheatricalRelease ?? null
  const hasRottenTomatoesScores = hubPageTheatricalRelease?.critics?.[0]?.scores?.some((score) => score?.value)

  // Client-side hook for region-personalized ticket status
  const isMovie = projectMeta.projectType === 'movie'
  const { ticketStatus } = useTheatricalTicketStatus({
    theatricalSlug: slug,
    region: region || 'US',
    skip: !isMovie,
  })
  const canShowTickets = isSellingTickets(ticketStatus)
  const ctas = filterToApplicableCtas((ctaSquaresCollection?.items as CallToAction[] | null) || [], isGuildMember)

  const { numberAvailable: guildTicketsCount = 0 } = useUserGuildTickets({
    theatricalSlug: slug,
    client,
    skip: !isGuildMember,
  })
  const hasEarlyAccess = useMemo(() => {
    return !loading && isGuildMember
  }, [isGuildMember, loading])

  const projectName = name as string

  const merchAnalytics = useMemo(() => {
    return { context: 'watch-theatrical', program: slug }
  }, [slug])
  const bgVideo = hubPageHeroBackgroundVideo || heroBackgroundVideo
  const heroBgImagePath = getHeroBackgroundImagePath(
    slug,
    projectMeta.title?.landscapeBackdrop?.cloudinaryPath,
    heroBackgroundImage,
  )
  const mobileHeroBgImagePath = getHeroBackgroundImagePath(
    slug,
    projectMeta.title?.portraitBackdrop?.cloudinaryPath,
    heroBackgroundImage,
  )
  const titleLogo = projectMeta.title?.logo ?? undefined
  const canShowTitleLogo = Boolean(titleLogo?.cloudinaryPath)
  const keyArt = projectMeta.title?.landscapeBackdrop?.cloudinaryPath ?? null
  const currentPhaseSlug = getCurrentPhaseSlug(props.watchableContent, ticketStatus)
  const totalVideos = (props.livestreams?.length || 0) + (additionalVideosCollection?.items?.length || 0)
  const canShowVideoGallery = trailerSourceUrl || totalVideos > 0
  const effectiveTheatricalReleaseDate =
    friendlyTheatricalReleaseDate ??
    formatReleaseDateString(hubPageTheatricalRelease?.region?.releaseDate) ??
    formatReleaseDate(releaseDate)
  const effectiveRawReleaseDate =
    filmDetails?.theatricalReleaseDate ?? hubPageTheatricalRelease?.region?.releaseDate ?? releaseDate

  const trackerReleaseDate = filmDetails?.theatricalReleaseDate ?? hubPageTheatricalRelease?.region?.releaseDate
  const trackerSlug = getGoalTrackerSlug(trackerReleaseDate)
  const trackerTitleContent = getGoalTrackerTitleContent(trackerReleaseDate)
  const trackerTitle = tCommon(trackerTitleContent.key, trackerTitleContent.defaultValue)

  useEffect(() => {
    let cancelled = false
    async function loadEpisodeStills() {
      if (projectMeta?.projectType === 'series' && projectMeta?.slug) {
        const stills = await fetchSeriesEpisodeStills(client, projectMeta.slug)
        if (!cancelled) {
          setEpisodesWithStills(stills)
        }
      } else {
        if (!cancelled) {
          setEpisodesWithStills(null)
        }
      }
    }
    loadEpisodeStills()
    return () => {
      cancelled = true
    }
  }, [client, projectMeta?.projectType, projectMeta?.slug])

  const galleryImages = useMemo(() => {
    if (projectMeta?.projectType === 'series' && episodesWithStills && episodesWithStills.length > 0) {
      const stillPaths = episodesWithStills
        .flatMap((episode) => [
          episode?.still1?.cloudinaryPath,
          episode?.still2?.cloudinaryPath,
          episode?.still3?.cloudinaryPath,
          episode?.still4?.cloudinaryPath,
        ])
        .filter(isDefined)
        .map((cloudinaryPath) => ({ cloudinaryPath, altText: null }))

      if (stillPaths.length > 0) {
        return stillPaths
      }
    }

    const titleStillPaths = [
      projectMeta?.title?.still1?.cloudinaryPath,
      projectMeta?.title?.still2?.cloudinaryPath,
      projectMeta?.title?.still3?.cloudinaryPath,
      projectMeta?.title?.still4?.cloudinaryPath,
    ]
      .filter(isDefined)
      .map((cloudinaryPath) => ({ cloudinaryPath, altText: null }))

    if (titleStillPaths.length > 0) return titleStillPaths

    return images || []
  }, [images, projectMeta, episodesWithStills])

  const bonhoefferDesktopBackground =
    'https://images.angelstudios.com/image/upload/c_fill,g_face,e_brightness_hsb:-10/v1723581778/studio-app/catalog/59b5abe4-7fb1-4d98-8d92-26621b40dc79'
  const bonhoefferMobileBackground =
    'https://images.angelstudios.com/image/upload/c_fill,g_face,e_brightness_hsb:-25/v1723581778/studio-app/catalog/18b839dd-3bf4-4cd3-a1d6-c30e7f77617b'

  useEffect(() => {
    switch (window?.location?.hash?.split('?')[0] || '') {
      case '#join-the-movement':
        if (joinTheMovementBannerVisited) return
        setJoinTheMovementBannerVisited(true)
        document.getElementById('join-the-movement')?.scrollIntoView({ behavior: 'smooth' })
        break
    }
  }, [joinTheMovementBannerVisited])

  // Lazy load video when section is near viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry?.isIntersecting) {
          setShouldLoadVideo(true)
          observer.disconnect() // Only need to trigger once
        }
      },
      { rootMargin: '200px' },
    )

    if (heroSectionRef.current) {
      observer.observe(heroSectionRef.current)
    }

    return () => observer.disconnect()
  }, [])

  // Pause hero video when out of view to save performance
  useEffect(() => {
    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        const video = entry.target as HTMLVideoElement
        if (entry.isIntersecting && entry.intersectionRatio >= 0.25) {
          video.play().catch(() => {
            // Video play was prevented, likely due to autoplay policies
          })
        } else {
          video.pause()
        }
      })
    }

    const observer = new IntersectionObserver(handleIntersection, {
      threshold: [0, 0.25], // Check at 0% and 25% visibility
      rootMargin: '0px',
    })

    if (heroVideoRef.current) {
      observer.observe(heroVideoRef.current)
    }

    return () => {
      observer.disconnect()
    }
  }, [shouldLoadVideo])
  return (
    <main className="relative">
      <section
        ref={heroSectionRef}
        aria-labelledby="cta-status-line"
        id="hero"
        className="relative h-screen w-full bg-cover"
      >
        {heroBgImagePath && (
          <div className="absolute inset-0 animate-fadein">
            {mobileHeroBgImagePath && (
              <ResponsiveBackgroundImage
                src={getCloudinaryImageUrl({
                  path: mobileHeroBgImagePath,
                  transforms: 'c_fill,g_face',
                })}
                className="!block lg:!hidden"
              />
            )}
            <ResponsiveBackgroundImage
              src={getCloudinaryImageUrl({
                path: heroBgImagePath,
                transforms: 'c_fill,g_face',
              })}
              className="!hidden lg:!block"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-core-gray-950" />
          </div>
        )}
        {bgVideo && shouldLoadVideo && (
          <video
            ref={heroVideoRef}
            className={classNames(
              'absolute inset-0 h-full w-full object-cover transition-opacity duration-1000',
              '[&::-webkit-media-controls]:!hidden [&::-webkit-media-controls-start-playback-button]:!hidden',
              videoReady ? 'opacity-100' : 'opacity-0',
            )}
            autoPlay
            playsInline
            muted
            loop
            preload="metadata"
            disablePictureInPicture
            disableRemotePlayback
            controls={false}
            src={bgVideo}
            onPlaying={() => setVideoReady(true)}
          />
        )}
        <div className="absolute bottom-[40px] left-1/2 flex max-h-screen w-full -translate-x-1/2 flex-col items-center justify-center gap-6">
          <Switch>
            <Case condition={canShowTitleLogo}>
              {titleLogo?.cloudinaryPath && (
                <BoundedImage
                  className="mx-4 mb-2 flex items-center justify-center md:justify-start"
                  alt={projectName}
                  path={titleLogo.cloudinaryPath}
                  width={500}
                  height={500}
                  imageClassName="!max-h-[250px] lg:max-h-[300px] lg:max-w-[600px] shortDesktop:max-h-[200px]"
                />
              )}
            </Case>
            <Case condition={!canShowTitleLogo && Array.isArray(logo)}>
              <div className="max-w-[500px] p-[25px] md:p-0">
                {Array.isArray(logo) && (
                  <Image
                    alt={projectName}
                    src={getCloudinaryImageUrl({
                      path: logo[0]?.secure_url ?? logo[0]?.url ?? '',
                      transforms: 'e_trim',
                    })}
                    width={logo[0]?.width}
                    height={logo[0]?.height}
                  />
                )}
              </div>
            </Case>
          </Switch>
          <CtaStatusLine
            heroCta={heroCta}
            isInTheaters={isInTheaters}
            isStreaming={isStreaming}
            canShowTickets={canShowTickets}
            friendlyTheatricalReleaseDate={effectiveTheatricalReleaseDate}
            theatricalReleaseDate={effectiveRawReleaseDate}
            filmDetails={filmDetails as FilmDetailsType}
            userRegionCode={userCountry}
            project={projectMeta}
            allTheatricalReleaseRegions={allTheatricalReleaseRegions}
            hasEarlyAccess={hasEarlyAccess}
            projectName={projectName}
          />
          <LazyHydrate whenVisible>
            <>
              <Ctas
                heroCta={heroCta}
                userRegionCode={userCountry}
                project={projectMeta}
                filmDetails={filmDetails as FilmDetailsType}
                allTheatricalReleaseRegions={allTheatricalReleaseRegions}
                hasEarlyAccess={hasEarlyAccess}
                isInTheaters={isInTheaters}
                isStreaming={isStreaming}
                isGuildMember={isGuildMember}
                numberAvailable={guildTicketsCount}
                canShowTickets={canShowTickets}
                currentPhaseSlug={currentPhaseSlug}
              />
              <a
                aria-label={t('learnMoreAboutShow', 'Learn More About {{projectName}}', { projectName })}
                href={ctas.length >= 1 ? '#get-involved' : '#film-details'}
                className="flex flex-col items-center justify-center"
              >
                <UpArrowCircleIcon className="rotate-180" color="white" />
              </a>
            </>
          </LazyHydrate>
        </div>
      </section>
      <nav id="sticky-cta" aria-labelledby="sticky-cta-title">
        <h2 className="sr-only" id="sticky-cta-title">
          <Translate i18nKey="joinTheGuildForTickets" t={t}>
            Join the Guild To Get Two Tickets
          </Translate>
        </h2>
        <StickyCta
          heroCta={heroCta}
          allTheatricalReleaseRegions={allTheatricalReleaseRegions}
          hasEarlyAccess={hasEarlyAccess}
          isInTheaters={isInTheaters}
          isStreaming={isStreaming}
          canShowTickets={canShowTickets}
          filmDetails={filmDetails as FilmDetailsType}
          projectName={projectName}
          userCountry={userCountry}
          titleLogo={titleLogo}
          projectMeta={projectMeta}
          logo={logo}
          currentPhaseSlug={currentPhaseSlug}
        />
      </nav>
      <section id="cta-collection" aria-labelledby="get-involved-section">
        <h2 id="get-involved-section" className="sr-only">
          <Translate i18nKey="getInvolved" t={t}>
            Get Involved
          </Translate>
        </h2>
        <PaddedContainer className="!px-0">
          <CtaSquareCollection
            numberAvailable={guildTicketsCount}
            client={client}
            currentPhaseSlug={currentPhaseSlug}
            ticketStatus={ticketStatus}
            watchableContent={props.watchableContent}
          />
        </PaddedContainer>
      </section>
      <GoalTrackerWidget
        trackerSlug={trackerSlug}
        title={trackerTitle}
        goalTrackers={goalTrackers}
        className="mx-auto max-w-3xl px-10 md:px-5 lg:mt-8 xl:max-w-5xl"
        isDarkMode
      />

      <When condition={Boolean(props.whereToWatch)}>
        <WhereToWatchSection
          projectName={projectName}
          projectSlug={projectMeta.slug}
          whereToWatch={props.whereToWatch}
        />
      </When>

      {investmentBanner && (
        <InvestmentBanner {...(investmentBanner as InvestmentBannerContent)} className="py-8 lg:mt-12" hideTrailer />
      )}

      <When condition={slug === slugs.bonhoeffer}>
        <section className="bg-black px-4 pt-4 lg:mt-20 lg:px-0 lg:pt-0" id="join-the-movement">
          <div className="relative flex min-h-[224px] items-center overflow-hidden rounded-2xl px-4 py-8 lg:min-h-[394px] lg:rounded-none lg:px-[120px] lg:py-20 2xl:min-h-[540px] 2xl:px-[160px] 3xl:min-h-[980px]">
            <ResponsiveBackgroundImage
              src={bonhoefferDesktopBackground}
              srcUrls={{
                xs: bonhoefferMobileBackground,
                sm: bonhoefferMobileBackground,
                md: bonhoefferMobileBackground,
                lg: bonhoefferDesktopBackground,
                xl: bonhoefferDesktopBackground,
                '2xl': bonhoefferDesktopBackground,
                '3xl': bonhoefferDesktopBackground,
              }}
              className="h-full w-full bg-black bg-cover bg-center object-cover"
            />
            <div className="relative flex flex-col items-start">
              <HeadingXS weight="bold" color="white" className="lg:photon-heading-large mb-1 lg:mb-3">
                {t('joinTheMovement', 'Join The Movement')}
              </HeadingXS>
              <CaptionSM
                weight="medium"
                color="core-gray-100"
                className="lg:photon-paragraph-xl max-w-[196px] lg:max-w-[333px]"
              >
                {t(
                  'signBonhoefferDeclaration',
                  'Sign the Bonhoeffer Declaration and declare your commitment to reject all forms of racism and antisemitism.',
                )}
              </CaptionSM>
              <LinkButton variant="white" href={paths.external.bonhoefferDeclaration.index} className="mt-4 w-fit">
                {t('signTheDeclaration', 'Sign the Declaration')}
              </LinkButton>
            </div>
          </div>
        </section>
      </When>

      <When condition={Boolean(critics?.items?.length || hasRottenTomatoesScores)}>
        <section aria-labelledby="review-section-title" key="reviews" className="bg-black">
          <PaddedContainer>
            <div className="w-full bg-black py-5 lg:py-[80px]">
              <Reviews theatricalRelease={hubPageTheatricalRelease} />
            </div>
          </PaddedContainer>
        </section>
      </When>

      <section aria-labelledby="livestream-section-title" className="bg-black">
        <LivestreamLink
          filmDetails={filmDetails as FilmDetailsType}
          projectName={projectName}
          projectId={projectMeta.id}
        />
      </section>

      <When condition={canShowVideoGallery}>
        <section
          aria-labelledby="video-section-title"
          key="video-gallery"
          id="video-gallery"
          className="w-full bg-black py-5 lg:py-[80px]"
        >
          <PaddedContainer>
            <VideoGallery
              trailerUrl={trailerSourceUrl}
              trailerTitle={t('filmTrailer', '{{projectName}} Trailer', { projectName })}
              additionalVideos={additionalVideosCollection as FilmDetailsAdditionalVideosCollection}
              livestreams={
                props.livestreams && props.livestreams.toSorted((a, b) => (a?.url ?? '').localeCompare(b?.url ?? ''))
              }
              keyArt={keyArt}
            />
          </PaddedContainer>
        </section>
      </When>

      {projectMeta.title && projectMeta.projectType !== 'special' && (
        <section aria-labelledby="about-the-show" id="film-details" key="film-details">
          <h2 id="about-the-show" className="sr-only">
            <Switch>
              <Case condition={projectMeta.projectType === 'movie'}>
                <Translate i18nKey="aboutTheFilm" t={t}>
                  About the Film
                </Translate>
              </Case>
              <Case condition={projectMeta.projectType === 'series'}>
                <Translate i18nKey="aboutTheSeries" t={t}>
                  About the Series
                </Translate>
              </Case>
              <Default>
                <Translate i18nKey="aboutTheShow" t={t}>
                  About the Show
                </Translate>
              </Default>
            </Switch>
          </h2>
          <PaddedContainer>
            <FilmDetails
              filmDetails={filmDetails as FilmDetailsType}
              slug={slug}
              projectType={projectMeta.projectType}
              guildScore={projectMeta?.highestScore}
            />
          </PaddedContainer>
        </section>
      )}

      <section aria-labelledby="franchise-section-title" className="bg-black">
        <FranchiseLink projectName={projectName} franchise={franchise} projectType={projectMeta?.projectType} />
      </section>

      {hasPressPage && (
        <section aria-labelledby="media-kits-title" className="bg-black lg:py-16">
          <h2 id="media-kits-title" className="sr-only">
            <Translate i18nKey="mediaKits" t={t}>
              Media Kits
            </Translate>
          </h2>
          <PressLinksSection
            projectName={projectName}
            projectSlug={slug}
            contentfulProjectTheme={props.contentfulProjectTheme}
          />
        </section>
      )}
      <section aria-label="get-updates-title">
        <GetUpdatesCta slug={slug} projectType={projectMeta.projectType} currentPhaseSlug={currentPhaseSlug} />
      </section>
      {Boolean(galleryImages?.length) && (
        <section aria-labelledby="image-gallery-title" className="mt-5 w-full bg-black py-5">
          <PaddedContainer className="overflow-x-hidden bg-black !px-0" fluid>
            <div key="image-gallery overflow-x-hidden">
              <div className="bg-black py-10 lg:pb-20">
                <div className="mb-10 text-center">
                  <HeadingXL id="image-gallery-title" as={AsH2} weight="bold" color="white">
                    {t('imageGallery', 'Image Gallery')}
                  </HeadingXL>
                </div>
                <LazyHydrate whenVisible>
                  <ImageGallery images={galleryImages} />
                </LazyHydrate>
              </div>
            </div>
          </PaddedContainer>
        </section>
      )}
      {shopifyStore && shopifyStore.storeUrl && shopifyStore.accessToken && shopifyStore.handle && (
        <section
          aria-labelledby="merch-gallery-title"
          key="merch-gallery"
          className="w-full overflow-x-hidden bg-black pt-20"
        >
          <LazyHydrate whenVisible>
            <div className="bg-black">
              <div className="mx-auto scroll-mt-[73px] text-white">
                <HorizontalShopifyMerchScroller
                  storeUrl={shopifyStore.storeUrl}
                  accessToken={shopifyStore.accessToken}
                  handle={shopifyStore.handle}
                  storeHref={shopifyStore?.storeHref}
                  analytics={merchAnalytics}
                  slug={slug}
                  padding
                />
              </div>
            </div>
          </LazyHydrate>
        </section>
      )}
      <section aria-labelledby="share-section-title" id="share-section" className="overflow-hidden bg-black pt-20">
        <LazyHydrate whenVisible>
          <ShareSection
            projectName={projectName}
            theatricalSlug={slug}
            shareUrl={shareOverrideUrl ?? asPath}
            projectType={projectMeta.projectType}
            className="text-center"
          />
        </LazyHydrate>
      </section>
      <section aria-labelledby="faq-section-title" key="faqs" className="overflow-x-hidden bg-black pb-40 pt-20">
        <div id="faqs" className="mx-auto max-w-[860px] scroll-mt-[73px] px-8 text-white">
          <LazyHydrate whenVisible>
            <FaqSection />
          </LazyHydrate>
        </div>
      </section>

      <When condition={canShowTickets && !isStreaming && !isGuildMember}>
        <GuildPromotionFloatingCta
          userRegionCode={userCountry}
          project={projectMeta}
          filmDetails={filmDetails as FilmDetailsType}
          allTheatricalReleaseRegions={allTheatricalReleaseRegions}
          hasEarlyAccess={hasEarlyAccess}
          isGuildMember={isGuildMember}
          titleLogo={titleLogo}
        />
      </When>
    </main>
  )
}

interface HeroBackgroundImage {
  url?: string
  width?: number
  height?: number
}

export function getHeroBackgroundImagePath(
  slug: string,
  backdropPath: string | undefined | null,
  heroBackgroundImage: Maybe<HeroBackgroundImage[]>,
): string | null {
  // Special handling for David - always use contentful image
  if (slug === slugs.david && Array.isArray(heroBackgroundImage) && heroBackgroundImage[0]?.url) {
    return heroBackgroundImage[0].url
  }

  // Use federation title backdrop
  if (backdropPath) {
    return backdropPath
  }

  // Fallback to contentful heroBackgroundImage
  if (Array.isArray(heroBackgroundImage) && heroBackgroundImage[0]?.url) {
    return heroBackgroundImage[0].url
  }

  return null
}

function formatReleaseDate(date: Date | null): string | null {
  if (!date) return null
  return Intl.DateTimeFormat(undefined, { month: 'long', day: 'numeric', timeZone: 'UTC' }).format(date)
}

function formatReleaseDateString(dateStr: string | null | undefined): string | null {
  if (!dateStr) return null
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return null
  return Intl.DateTimeFormat(undefined, { month: 'long', day: 'numeric', timeZone: 'UTC' }).format(date)
}
